import { NiceModalHandler } from "@ebay/nice-modal-react";
import { uniqBy } from "lodash-es";
import { useState } from "react";
import { SendEmailMutation } from "src/api/Email";
import { BorderButton } from "src/components/buttons/BorderButton";
import {
  EmailTemplateMove,
  renderMove,
  renderRegistration,
} from "src/components/email/EmailTemplate";
import UserModel from "src/models/UserModel";
import { useApiMutation } from "src/stores/Api";
import { useStore } from "src/stores/Store";
import { formatPhoneNumber } from "src/utils/formatPhone";
import message from "src/utils/message";
import { modals, useModal } from "src/utils/modals";
import renderEmail, { EmailTemplate } from "src/utils/renderEmail";
import tw from "src/utils/tw";
import { sendEmailVariables } from "types/code-generator";
import { EmailRegistrations } from "types/email-registrations";
import InputTextarea from "../inputs/InputTextarea";
import Modal from "./Modal";

export interface Attachment {
  id: string;
  filename: string;
  path: string;
}

interface Props {
  allowEdit?: boolean;
  allowSend?: boolean;
  attachments?: Attachment[];
  handleCancel?: () => void;
  handleOk?: () => void;
  htmlText?: string;
  onUserMessageUpdate?: (modal, userMessage: string) => void;
  smsText?: string;
  subject?: string;
  template?: EmailTemplate;
  toEmailUsers: {
    id: string;
    email: string;
  }[];
  toSmsUsers?: UserModel[];
  type?: string;
  useUserEmail?: boolean;
  userMessage?: string;
  variables?: any;
  title?: string;
  fromEmail?: string;
  registrations?: EmailRegistrations[];
  move?: EmailTemplateMove;
  hideCloseButton?: boolean;
  smsOnly?: boolean;
  modal?: NiceModalHandler;
  hideNotification?: boolean;
  confirmButtonText?: string;
  cancelButtonText?: string;
  cancelButtonOk?: boolean;
}

export function PreviewEmailBody(props: Props) {
  const {
    attachments,
    template,
    variables,
    subject,
    type,
    smsText,
    htmlText,
    useUserEmail,
    registrations,
  } = props;
  const [disabled, setDisabled] = useState(false);
  const store = useStore();
  const sendEmail = useApiMutation(SendEmailMutation);

  async function sendEmailMessage() {
    let emailData;

    if (subject && htmlText) {
      emailData = {
        subject,
        type,
        smsText,
        htmlText,
      };
    } else {
      emailData = renderEmail({ template, variables });
    }

    // console.log("emailData", emailData);

    const emailVariables: sendEmailVariables = {
      data: {
        users: uniqBy(props.toEmailUsers, "id").map((u) => u.id),
        organizationId: store.organization.id,
        subject: emailData.subject,
        files: attachments?.map((a) => a.id) || [],
        type: emailData.type,
        smsText: emailData.smsText,
        html: emailData.htmlText,
      },
    };

    if (useUserEmail) {
      emailVariables.data.replyTo = store.user.email;
    }

    if (registrations) {
      emailVariables.data.registrations = registrations;
    }

    if (props.move) {
      emailVariables.data.move = props.move;
    }

    if (emailVariables.data.users.length === 0) {
      message.error("No recipients");

      handleCancel();

      return;
    }

    setDisabled(true);

    let processingMessage;

    if (!props.hideNotification) {
      processingMessage = message.loading("Sending email");
    }

    try {
      await sendEmail({
        variables: emailVariables,
      });

      // if (shouldSendSms) {
      //   await sendSms({
      //     variables: uniqBy(props.toSm, "id").map((u) => u.id),
      //     organizationId: store.organization.id,
      //   });
      // }

      if (processingMessage) {
        processingMessage();
        message.success("Email sent");
      }

      setDisabled(false);

      if (props.handleOk) {
        props.handleOk();
      }

      handleCancel();
    } catch (e) {
      message.error(e);
      setDisabled(false);
    }
  }

  function onChangeText(e) {
    const userMessage = e;

    if (props.onUserMessageUpdate) {
      props.onUserMessageUpdate(props.modal, userMessage);
    }
  }

  function handleCancel() {
    if (props.handleCancel) {
      props.handleCancel();
    }

    if (props.modal) {
      props.modal.hide();
    }
  }

  let emailData;

  if (props.subject && htmlText) {
    emailData = {
      subject: props.subject,
      htmlText,
    };
  } else if (template && variables) {
    emailData = renderEmail({ template, variables });
  }

  if (!emailData) {
    return null;
  }

  let body = emailData.htmlText;
  let from = (
    <>
      {store.organization.title}{" "}
      <span className="text-sm">({store.organization.email})</span>
    </>
  );
  const emailUsers = uniqBy(props.toEmailUsers, "id");
  let to = emailUsers
    .filter((u) => u.email)
    .map((u) => u.email)
    .join(", ");
  let registrationsData;

  if (props.fromEmail) {
    from = (
      <>
        {store.organization.title}{" "}
        <span className="text-sm">({props.fromEmail})</span>
      </>
    );
  }

  if (props.smsOnly) {
    from = <>{formatPhoneNumber(process.env.NEXT_PUBLIC_TWILIO_FROM)}</>;
    to = `${props.toSmsUsers.length} enrolled accounts`;
    body = emailData.smsText;
  }

  if (to.length > 40) {
    to = `${props.toEmailUsers.length} enrolled accounts`;
  }

  if (registrations) {
    registrationsData = (
      <div className="-mx-4 border-t border-borderGray bg-bgGray p-4">
        <table width="100%">
          <tbody>{registrations.map((r, i) => renderRegistration(r))}</tbody>
        </table>
      </div>
    );
  }

  if (props.move) {
    registrationsData = (
      <div className="-mx-4 border-t border-borderGray bg-bgGray p-4">
        {renderMove(props.move)}
      </div>
    );
  }

  const footer = props.allowSend ? (
    <>
      <BorderButton
        type="submit"
        onClick={sendEmailMessage}
        disabled={disabled}
      >
        {props.confirmButtonText || "Send Email"}
      </BorderButton>
      <BorderButton
        onClick={
          props.cancelButtonOk
            ? async () => {
                const processingMessage = message.loading("Processing…");
                setDisabled(true);
                await props.handleOk();
                processingMessage();
                setDisabled(false);
                handleCancel();
              }
            : handleCancel
        }
        disabled={disabled}
      >
        {props.cancelButtonText || "Don’t Send"}
      </BorderButton>
    </>
  ) : (
    <BorderButton onClick={handleCancel} disabled={disabled}>
      Close Preview
    </BorderButton>
  );

  const content = (
    <div className={tw(!props.modal && "flex h-full flex-col overflow-hidden")}>
      <div>
        <div className="flex border-b border-borderGray">
          <div className="w-1/4 border-r border-borderGray bg-bgGray p-4 text-empty">
            <strong>From</strong>
          </div>
          <div className="flex-1 p-4">{from}</div>
        </div>
        <div className="flex border-b border-borderGray">
          <div className="w-1/4 border-r border-borderGray bg-bgGray p-4 text-empty">
            <strong>To</strong>
          </div>
          <div className="flex-1 p-4">{to}</div>
        </div>

        {!props.smsOnly && (
          <div className="flex border-b border-borderGray">
            <div className="w-1/4 border-r border-borderGray bg-bgGray p-4 text-empty">
              <strong>Subject</strong>
            </div>
            <div className="flex-1 p-4">{emailData.subject}</div>
          </div>
        )}

        <div className="border-b border-borderGray bg-bgGray p-4 text-empty">
          <strong>{props.smsOnly ? "Text" : "Email"} Body</strong>
        </div>
      </div>

      <div className={tw(!props.modal && "flex-1 overflow-auto")}>
        {store.organization.logo && !props.smsOnly && (
          <div className="px-4 pt-4">
            <img
              css={{ width: 180 }}
              src={store.organization.logo.url}
              alt={store.organization.title}
              className="rounded"
            />
          </div>
        )}

        {props.allowEdit && (
          <div
            className="mb-4 max-h-96 overflow-auto"
            css={{
              overflowWrap: "break-word",
              wordWrap: "break-word",
            }}
          >
            <div
              className="pointer-events-none"
              dangerouslySetInnerHTML={{ __html: body }}
            />
            {registrationsData}
          </div>
        )}

        {!props.allowEdit && (
          <>
            <div className="px-4 pt-4">
              {props.smsOnly && props.userMessage && (
                <div className="text-xs text-empty">SMS Message 1</div>
              )}
              <div>
                <div
                  dangerouslySetInnerHTML={{ __html: body }}
                  className="pointer-events-none"
                />
                {registrationsData}
              </div>
            </div>
          </>
        )}

        {!props.allowEdit && props.userMessage && props.smsOnly && (
          <>
            <div className="px-4">
              <div className="pt-4 text-xs text-empty">SMS Message 2</div>
              <div dangerouslySetInnerHTML={{ __html: props.userMessage }} />
            </div>
          </>
        )}

        {props.allowEdit && (
          <div className="mb-4">
            <InputTextarea
              disabled={disabled}
              value={props.userMessage}
              onChange={onChangeText}
              placeholder="Include a custom message"
            />
          </div>
        )}

        {attachments && attachments.length > 0 && (
          <>
            <div className="pt-4">
              <hr />
              {attachments.map((a) => {
                return (
                  <p key={a.path}>
                    <i aria-hidden className="far fa-paperclip" /> {a.filename}
                  </p>
                );
              })}
              <hr />
            </div>
          </>
        )}

        {props.smsOnly && (
          <>
            <hr />
            <p className="px-4 text-sm text-empty">
              <strong className="text-error">
                You will not receive text replies.
              </strong>
              <br />
              Bulk texts are for urgent notifications.
              <br />
              Two-way texting is disabled.
            </p>
          </>
        )}
      </div>

      {!props.modal && (
        <div
          className="flex gap-4 bg-white p-4"
          css={{
            boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
          }}
        >
          {footer}
        </div>
      )}
    </div>
  );

  if (props.modal) {
    return (
      <Modal
        visible={props.modal.visible}
        handleCancel={handleCancel}
        afterClose={props.modal.remove}
        hideClose={props.hideCloseButton}
        footer={footer}
        title={
          props.title
            ? props.title
            : props.smsOnly
              ? "Text Preview"
              : "Email Preview"
        }
      >
        <div className="-m-4 overflow-hidden">{content}</div>
      </Modal>
    );
  }

  return content;
}

export const PreviewEmailModal = modals.create((props: Props) => {
  const modal = useModal();

  return <PreviewEmailBody {...props} modal={modal} />;
});
